/**
 * 使用状況の pill バッジ (マスタ一覧の「使用地区」「使用テンプレート」列で共用)。
 *
 * AI 解析テンプレ (M1) 一覧の使用地区表示で使っていた見た目を、AI 出力フォーマット (M3)
 * 一覧からも同じ形で使えるよう切り出したもの (二重実装の回避)。0 件のときは
 * 「未使用」のグレー文字を出し、どこからも使われていないマスタが一目で分かるようにする。
 */

/** バッジの色味。district=使用地区 (緑) / template=使用テンプレート (青) / muted=無効・アーカイブ済 (灰)。 */
export type UsageBadgeTone = 'district' | 'template' | 'muted';

export interface UsageBadgeItem {
  /** React key。 */
  key: string;
  /** バッジ本文 (地区名 / テンプレート名など、管理者が読む日本語名)。 */
  label: string;
  /** 本文の後ろに小さく添える補足 (例:「直接指定」「アーカイブ済み」)。 */
  note?: string;
  tone?: UsageBadgeTone;
  /** マウスオーバーで出す説明 (経路の詳細など)。 */
  title?: string;
}

const TONE_CLASS: Record<UsageBadgeTone, string> = {
  district: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  template: 'bg-sky-50 border-sky-200 text-sky-800',
  muted: 'bg-slate-50 border-slate-200 text-slate-500',
};

export interface UsageBadgesProps {
  items: UsageBadgeItem[];
  /** 0 件のときの文言 (既定「未使用」)。 */
  emptyLabel?: string;
}

export function UsageBadges({ items, emptyLabel = '未使用' }: UsageBadgesProps) {
  if (items.length === 0) {
    return <span className="text-slate-400 text-xs">{emptyLabel}</span>;
  }
  return (
    // min-w-0 + break-all: 表示名が引けないときはドキュメント ID がそのまま出るため、
    // 途中で折り返せないと 1 個のバッジがコンテナを押し広げて画面幅を超える。
    <span className="flex flex-wrap gap-1 min-w-0">
      {items.map((item) => (
        <span
          key={item.key}
          title={item.title}
          className={`inline-block max-w-full break-all px-1.5 py-0.5 text-[11px] border rounded ${
            TONE_CLASS[item.tone ?? 'district']
          }`}
        >
          {item.label}
          {item.note && <span className="ml-1 opacity-70">（{item.note}）</span>}
        </span>
      ))}
    </span>
  );
}
